import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import Modal from 'react-modal';
import './Referral.css';
import { referralService } from '../Network/services/referralService';

const Referral = () => {
  const [referralData, setReferralData] = useState({
    referralCode: '',
    userName: '',
    amountEarned: 0,
    amountWithdrawn: 0,
    availableBalance: 0,
    referrals: []
  });

  const [referralSettings, setReferralSettings] = useState({
    individual_price: 15,
    business_price: 25,
    minimum_withdrawal_limit: 500
  });

  const [referralContent, setReferralContent] = useState([]);

  const [loading, setLoading] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [withdrawalModalOpen, setWithdrawalModalOpen] = useState(false);
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [amountError, setAmountError] = useState('');

  useEffect(() => {
    let isMounted = true;

    
    const fetchData = async () => {
      if (isMounted) {
        setLoading(true);
        try {
          const payload = new FormData();
          const response = await referralService.getReferralEarnings(payload);
          
          if (!isMounted) return;
          
          if (response.status === "200") {
            const data = response.data;
            
            const allReferrals = [
              ...(data.all_referred_users || []).map(user => ({
                ...user,
                status: 'JOINED',
                referral_amount: 0
              })),
              ...(data.redeemed_referrals || []).map(user => ({
                ...user,
                status: 'EARNED'
              }))
            ];

            setReferralData(prevData => ({
              ...prevData,
              referralCode: data.user_referral_code || prevData.referralCode,
              userName: data.user_name || prevData.userName,
              amountEarned: data.total_referral_earnings || 0,
              amountWithdrawn: data.total_withdrawn || 0,
              availableBalance: data.available_balance || 0,
              referrals: allReferrals
            }));

            if (data.referral_settings) {
              setReferralSettings({
                individual_price: data.referral_settings.individual_price || 15,
                business_price: data.referral_settings.business_price || 25,
                minimum_withdrawal_limit: data.referral_settings.minimum_withdrawal_limit || 500
              });

              if (data.referral_settings.refferal_content) {
                try {
                  const parsedContent = JSON.parse(data.referral_settings.refferal_content);
                  setReferralContent(Array.isArray(parsedContent) ? parsedContent : []);
                } catch (error) {
                  console.error('Error parsing referral content:', error);
                  setReferralContent([]);
                }
              }
            }
          } else {
            toast.error(response.message || 'Failed to fetch referral data');
          }
        } catch (error) {
          console.error('Error fetching referral data:', error);
          if (isMounted) {
            toast.error('Failed to fetch referral data');
          }
        } finally {
          if (isMounted) {
            setLoading(false);
          }
        }
      }
    };
    
    fetchData();
    
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const userData = localStorage.getItem('UserData');
    if (userData) {
      const user = JSON.parse(userData);
      setReferralData({
        referralCode: user.refferal_code || ''
      });
      
    }
  }, []);


  const openShareModal = () => {
    setShareModalOpen(true);
  };

  const closeShareModal = () => {
    setShareModalOpen(false);
  };

  const copyReferralMessage = () => {
    const message = `Hey! It's ${referralData.userName || 'User'} 👋\n\nI am using the app GigFWS for a while now; it helps me earn and also get the job done.\nYou should check it out!\nUse my referral code ${referralData.referralCode} when you sign up to get started.\n\nDownload it here:\nAndroid: https://play.google.com/store/apps/details?id=com.futureworkforce \nIOS: https://apps.apple.com/in/app/gigfws-post-apply-gig-jobs/id6752988240`; //'\niOS: https://apps.apple.com/app/gigfws'
    
    navigator.clipboard.writeText(message).then(() => {
      toast.success('Referral message copied to clipboard!');
      setShareModalOpen(false);
    }).catch(() => {
      toast.error('Failed to copy referral message');
    });
  };


  const openWithdrawalModal = () => {
    if (referralData.availableBalance < referralSettings.minimum_withdrawal_limit) {
      toast.error(`You need to earn at least ₹${referralSettings.minimum_withdrawal_limit} to withdraw to wallet`);
      return;
    }
    setWithdrawalModalOpen(true);
    setWithdrawalAmount(referralData.availableBalance.toString());
  };

  const closeWithdrawalModal = () => {
    setWithdrawalModalOpen(false);
    setWithdrawalAmount('');
    setAmountError('');
  };

  const handleSuggestedAmount = (amount) => {
    setWithdrawalAmount(amount.toString());
    setAmountError(''); // Clear error when selecting suggested amount
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    setWithdrawalAmount(value);
    
    // Validate amount
    if (value) {
      const amount = parseFloat(value);
      if (amount < referralSettings.minimum_withdrawal_limit) {
        setAmountError(`Minimum withdrawal amount is ₹${referralSettings.minimum_withdrawal_limit}`);
      } else if (amount > referralData.availableBalance) {
        setAmountError(`Amount cannot exceed available balance of ₹${referralData.availableBalance}`);
      } else {
        setAmountError('');
      }
    } else {
      setAmountError('');
    }
  };

  const processWithdrawal = async () => {
    if (!withdrawalAmount || parseFloat(withdrawalAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (amountError) {
      toast.error(amountError);
      return;
    }
    
    try {
      setLoading(true);
      const payload = new FormData();
      payload.append('withdrawal_amount', withdrawalAmount);
      
      const response = await referralService.addReferralAmountToWallet(payload);
      
      if (response.status === "200") {
        toast.success(response.message || 'Amount successfully added to wallet!');
        
        setReferralData(prevData => ({
          ...prevData,
          availableBalance: response.data.available_balance_after || 0,
          amountWithdrawn: response.data.total_withdrawn || 0
        }));
        
        closeWithdrawalModal();
      } else {
        toast.error(response.message || 'Failed to add amount to wallet');
      }
    } catch (error) {
      console.error('Error adding amount to wallet:', error);
      toast.error('Failed to add amount to wallet');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'JOINED':
        return '#007bff';
      case 'EARNED':
        return '#00B136';
     
      default:
        return '#6C757D';
    }
  };

  return (
    <div className="referral-container">
      <div className="content">
        <h1 className="title">Refer & Earn</h1>
        
        <div className="main-content">
          <div className="referral-section">
            <div className="referral-header">
              <div className="referral-icon">
               <img src="/assets/images/ShareOrReferIcon.svg" alt="Referral Icon" />
              </div>
              <h2 className="referral-title">Refer & Earn</h2>
              <p className="referral-description">
                Share your code and earn ₹50 when your friends post or finish a job.
              </p>
            </div>

            <div className="referral-link-section">
              <div className="referral-link-container">
                <div className="input-wrapper">
                  {loading ? (
                    <div className="referral-referral-skeleton-loader referral-skeleton-input"></div>
                  ) : (
                    <>
                      <input 
                        type="text" 
                        value={referralData.referralCode}
                        readOnly
                        className="referral-link-input"
                        data-validate="false"
                        data-rule=""
                        novalidate
                      />
                      <button 
                        className="copy-link-button"
                        onClick={openShareModal}
                      >
                        Get Link
                      </button>
                    </>
                  )}
                </div>
              </div>
              
            </div>

            <div className="how-it-works-section">
              <h3 className="how-it-works-title">How it Works</h3>
              <div className="steps-container">
                {referralContent.length > 0 ? (
                  referralContent.map((content, index) => (
                    <React.Fragment key={index}>
                      <div className="step">
                        <div className="step-number">{String(index + 1).padStart(2, '0')}</div>
                        <div className="step-content">
                          {content}
                        </div>
                      </div>
                      {index < referralContent.length - 1 && (
                        <div className="step-arrow">→</div>
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <>
                    <div className="step">
                      <div className="step-number">01</div>
                      <div className="step-content">
                        Invite your friends by sharing your referral code from the app.
                      </div>
                    </div>
                    <div className="step-arrow">→</div>
                    <div className="step">
                      <div className="step-number">02</div>
                      <div className="step-content">
                        When they join successfully and post their first job or complete, you earn
                      </div>
                    </div>
                    <div className="step-arrow">→</div>
                    <div className="step">
                      <div className="step-number">03</div>
                      <div className="step-content">
                        When your friend is an individual, you earn ₹{referralSettings.individual_price} and when your friend is a business owner, you earn ₹{referralSettings.business_price}
                      </div>
                    </div>
                    <div className="step-arrow">→</div>
                    <div className="step">
                      <div className="step-number">04</div>
                      <div className="step-content">
                        You can withdraw your earnings anytime when you reach ₹{referralSettings.minimum_withdrawal_limit}.
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="referral-sidebar">
            <div className="referrals-summary-card">
              <h3 className="card-title">Referrals</h3>
              <div className="referrals-summary-content">
                
              <div className="amounts-section">
                <div className="amount-earned">
                  {loading ? (
                    <>
                      <div className="referral-referral-skeleton-loader referral-skeleton-amount-large"></div>
                      <div className="referral-referral-skeleton-loader referral-skeleton-label"></div>
                    </>
                  ) : (
                    <>
                      <span className="amount earned">₹{referralData.availableBalance}</span>
                      <span className="amount-label">Claim Referral Amount</span>
                    </>
                  )}
                </div>
                <button 
                  className={`add-to-wallet-button ${referralData.availableBalance < referralSettings.minimum_withdrawal_limit ? 'disabled' : ''}`}
                  onClick={openWithdrawalModal}
                  disabled={referralData.availableBalance < referralSettings.minimum_withdrawal_limit}
                >
                  Add to Wallet
                </button>
              </div>
                {loading ? (
                  <div className="referral-referral-skeleton-loader referral-skeleton-label" style={{width: '200px', margin: '8px 0 15px 0'}}></div>
                ) : (
                  <p className="payout-info">Withdraw money once you reach ₹{referralSettings.minimum_withdrawal_limit}.</p>
                )}
              <div className="separator-line"></div>
              <div className="payout-section">
                <div className="amount-withdrawn">
                  {loading ? (
                    <>
                      <div className="referral-referral-skeleton-loader referral-skeleton-amount"></div>
                      <div className="referral-referral-skeleton-loader referral-skeleton-label"></div>
                    </>
                  ) : (
                    <>
                      <span className="amount withdrawn">₹{referralData.amountEarned}</span>
                      <span className="amount-label">Total Earned Amount</span>
                    </>
                  )}
                </div>
                <div className="amount-withdrawn">
                  {loading ? (
                    <>
                      <div className="referral-referral-skeleton-loader referral-skeleton-amount"></div>
                      <div className="referral-referral-skeleton-loader referral-skeleton-label"></div>
                    </>
                  ) : (
                    <>
                      <span className="amount withdrawn">₹{referralData.amountWithdrawn}</span>
                      <span className="amount-label">Money Withdrawed</span>
                    </>
                  )}
                </div>
              </div>
              </div>
            </div>

            <div className="referrals-list-card">
              <h3 className="card-title">Your Referrals {loading ? (
                <span className="referral-skeleton-loader" style={{width: '30px', height: '18px', display: 'inline-block', marginLeft: '8px'}}></span>
              ) : (
                <span className="referrals-count">({String(referralData.referrals?.length || 0).padStart(2, '0')})</span>
              )}</h3>
              <div className="referrals-list">
                {loading ? (
                  <div className="loading-message">
                    <div className="referral-spinner"></div>
                    <div>Loading referrals...</div>
                  </div>
                ) : referralData.referrals?.length > 0 ? (
                  referralData.referrals.map((referral, index) => (
                    <div key={referral.referral_record_id || referral.user_id || index} className="referral-item">
                      <div className="referral-info">
                        <div className="referral-id">ID: {referral.user_id}</div>
                        <div className="referral-name">{referral.name}</div>
                        <div className="referral-date">
                          {referral.status === 'JOINED' 
                            ? `Joined on ${new Date(referral.joined_date).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              })}`
                            : `Referred on ${new Date(referral.referral_date).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              })}`
                          }
                        </div>
                      </div>
                      <div className="referral-status">
                        <div 
                          className="status-text"
                          style={{ color: getStatusColor(referral.status) }}
                        >
                          {referral.status}
                        </div>
                        {referral.referral_amount > 0 && (
                          <div className="referral-amount">₹{referral.referral_amount}</div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-referrals-message">No referrals found</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={shareModalOpen}
        onRequestClose={closeShareModal}
        style={customModalStyles}
        contentLabel="Share Referral Modal"
        scrollable={true}
      >
        <div className="share-referral-modal">
          <div className="share-referral-container">
            <div className="share-referral-header">
              <h2 className="share-referral-title">Share Your Referral Code</h2>
              <button 
                type="button" 
                className="close-btn" 
                onClick={closeShareModal}
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            
            <div className="share-referral-content">
              <div className="referral-message">
                <p>Want to share your referral code with friends? Copy the message below and send it through your preferred channel.</p>
              </div>
              
              <div className="referral-link-section">
                <div className="link-container">
                  <div 
                    className="referral-message-display"
                    contentEditable={false}
                  >
                    Hey! It's {referralData.userName || 'User'} 👋<br/><br/>
                    I am using the app <span className="app-name">GigFWS</span> for a while now; it helps me earn and also get the job done.<br/>
                    You should check it out!<br/>
                    Use my referral code <span className="referral-code-highlight">{referralData.referralCode}</span> when you sign up to get started.<br/><br/>
                    <span className="download-text">Download it here:</span><br/>
                    Android: <a href="https://play.google.com/store/apps/details?id=com.futureworkforce" className="app-link">https://play.google.com/store/apps/details?id=com.gigfws</a><br/>
                    iOS: <a href="https://apps.apple.com/in/app/gigfws-post-apply-gig-jobs/id6752988240" className="app-link">https://apps.apple.com/app/gigfws</a> 
                  </div>
                </div>
                
                <button 
                  className="copy-referral-btn"
                  onClick={copyReferralMessage}
                >
                  Copy Message
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={withdrawalModalOpen}
        onRequestClose={closeWithdrawalModal}
        style={customModalStyles}
        contentLabel="Withdrawal Modal"
        scrollable={true}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
      >
        <div className="withdrawal-modal">
          <div className="withdrawal-container">
            <div className="withdrawal-header">
              <div className="withdrawal-icon">
                  <img src="/assets/images/WidthDrawnAmountIcon.svg" alt="Coin Icon" />
              </div>
              <h2 className="withdrawal-title">Withdraw Money to Wallet</h2>
              <p className="withdrawal-description">Transfer your earned balance to your wallet.</p>
              <button 
                type="button" 
                className="close-btn" 
                onClick={closeWithdrawalModal}
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            
            <div className="withdrawal-content">
              <div className="amount-input-section">
                <div className={`amount-input-container ${amountError ? 'invalid-amount' : ''}`}>
                  <span className="currency-symbol">₹</span>
                  <input
                    type="number"
                    value={withdrawalAmount}
                    onChange={handleAmountChange}
                    placeholder="Enter Amount"
                    className="amount-input"
                    min={referralSettings.minimum_withdrawal_limit}
                    max={referralData.availableBalance}
                  />
                </div>
                {amountError && (
                  <div className="amount-error">
                    {amountError}
                  </div>
                )}
                
                <div className="suggested-amounts">
                  <button 
                    className="suggested-amount-btn"
                    onClick={() => handleSuggestedAmount(referralSettings.minimum_withdrawal_limit)}
                  >
                    ₹{referralSettings.minimum_withdrawal_limit}
                  </button>
                  <button 
                    className="suggested-amount-btn"
                    onClick={() => handleSuggestedAmount(referralSettings.minimum_withdrawal_limit + 100)}
                  >
                    ₹{referralSettings.minimum_withdrawal_limit + 100}
                  </button>
                  <button 
                    className="suggested-amount-btn"
                    onClick={() => handleSuggestedAmount(referralData.availableBalance)}
                  >
                    ₹{referralData.availableBalance}
                  </button>
                </div>
              </div>
              
              <div className="withdrawal-actions">
                <button 
                  className="withdraw-button"
                  onClick={processWithdrawal}
                  disabled={loading || !withdrawalAmount || parseFloat(withdrawalAmount) < referralSettings.minimum_withdrawal_limit}
                >
                  {loading ? 'Processing...' : 'Add to Wallet'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const customModalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
    height: "100%",
    background: "transparent",
    border: "none",
    padding: "0",
  },
  overlay: { 
    zIndex: 1000,
    backgroundColor: "rgba(0, 0, 0, 0.5)"
  },
};

const mapStateToProps = (state) => ({
  // Add any state props you need
});

const mapDispatchToProps = (dispatch) => ({
  dispatch,
});

export default connect(mapStateToProps, mapDispatchToProps)(Referral);
